// Global React error boundary. Any uncaught render error fires a structured
// POST to /api/log/event with event="error" — the shape spec §13.8 calls out.
// Telemetry failure is silent: a broken event endpoint must never block the
// fallback UI from rendering.

import { Component, type ErrorInfo, type ReactNode } from "react";

import { apiFetch } from "./client";

interface Props {
  children: ReactNode;
  fallback?: (error: Error) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    void apiFetch("/api/log/event", {
      method: "POST",
      body: JSON.stringify({
        level: "error",
        event: "error",
        payload: {
          message: error.message,
          // Trim the stack so a runaway component tree doesn't bloat the log line.
          stack: (error.stack ?? "").slice(0, 4000),
          componentStack: (info.componentStack ?? "").slice(0, 2000),
          route: typeof window !== "undefined" ? window.location.pathname : "",
        },
      }),
    }).catch(() => {
      // Telemetry is fire-and-forget (spec §13.8). Drop on failure.
    });
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.state.error);

    return (
      <main
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: "2rem",
          fontFamily: "var(--font-body)",
          color: "var(--text)",
          background: "var(--bg)",
        }}
      >
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          <h1 style={{ marginBottom: "0.5rem" }}>Something went wrong.</h1>
          <p style={{ color: "var(--text-dim)" }}>
            The error has been logged. Try reloading the page.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: "1.25rem",
              background: "var(--accent)",
              color: "var(--accent-text)",
              border: "none",
              borderRadius: "var(--radius)",
              padding: "0.5rem 1rem",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      </main>
    );
  }
}
